import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import {
  MyTable,
  MyButton,
  MyModal,
  DynamicForm,
  DeleteConfirmModal,
} from '../../index';
import SearchBar from '../SearchBar/SearchBar';
import TableFilters from '../TableFilters/TableFilters';
import TablePagination from '../TablePagination/TablePagination';
import TableLoading from '../TableLoading/TableLoading';
import { generateFiltersFromData } from '../../../utils';
import { exportTableToCSV } from '../../../utils/exportUtils';
import { dataTableContainerStyles } from './DataTableContainer.styles';
import { useTags } from '../../../hooks';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

// Türkçe karakter uyumlu arama için normalize fonksiyonu
const normalizeSearchString = (value) => {
  if (value == null) return '';
  const str = value.toString();
  const map = {
    'I': 'i', 'İ': 'i', 'ı': 'i',
    'Ş': 's', 'ş': 's',
    'Ğ': 'g', 'ğ': 'g',
    'Ü': 'u', 'ü': 'u',
    'Ö': 'o', 'ö': 'o',
    'Ç': 'c', 'ç': 'c',
  };
  return str
    .split('')
    .map(ch => map[ch] || ch)
    .join('')
    .toLowerCase();
};

const DataTableContainer = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const { tags } = useTags();
  const {
    title,
    columns = [],
    data = [],
    showSearch = true,
    showFilters = true,
    showPagination = true,
    showAddButton = true,
    addButtonText = 'Ekle',
    addButtonIcon = <AddIcon />,
    addButtonSx = {},
    filterConfigs = [],
    searchPlaceholder = 'Ara...',
    rowsPerPage = 20,
    loading = false,
    loadingRows = 5,
    currentPage = 1,
    searchValue = '',
    searchFields = [],
    enableAutoFilter = true,
    formFields = [],
    addModalTitle = 'Yeni Kayıt Ekle',
    editModalTitle = 'Kayıt Düzenle',
    entityType = 'record',
    deleteModalIcon,
    onFormSubmit,
    onAddButtonClick,
    rowActions = [],
    onRowAction,
    transformEditData,
    renderFieldExtras,
    onDelete,
    onRowClick,
    submitting = false,
    deleting = false,
    deleteConfirmText,
    // Export props (optional)
    exportButton = true, // Default to true - always show export button
    onExport, // Custom export function (optional)
    // Async callback that returns ALL rows matching the active filters,
    // bypassing pagination. Used by CSV export when serverPagination is on
    // so the user doesn't get just the current page.
    onFetchAllForExport,
    exportDisabled = false,
    exportLabel = 'table.actions.export',
    exportFilename = null, // Custom filename (optional)
    // Comment section
    commentEntityType = null,
    // Row styling
    getRowSx,
    // ---- Backend pagination (Task 3) ----
    // When serverPagination is true, the container delegates page/pageSize
    // state to the parent hook (typically useDynamicEntity). Client-side
    // slicing is skipped and the server-provided total count drives the
    // pagination UI. When false, existing client-side behavior is preserved.
    serverPagination = false,
    serverPage = 1,
    serverPageSize = 20,
    serverTotalCount = 0,
    serverHasNextPage = false,
    onServerPageChange,
    onServerPageSizeChange,
    // ---- Backend filtering / search ----
    // When serverFilters is true, the container still owns filter & search UI
    // state but DOES NOT apply them client-side. Instead it bubbles changes up
    // via onFiltersChange so the parent can translate them into a Hasura
    // `_bool_exp` (see utils/buildHasuraWhere.js) and feed it into the entity
    // hook's `setExtraWhereCondition`. Sort stays client-side (it only
    // reorders the rows already loaded for the current page).
    serverFilters = false,
    onFiltersChange,
    // Optional slot rendered inline in the header (between search and the
    // right-side action buttons). Use for page-specific toggles like
    // "Show completed" without breaking the toolbar layout.
    headerExtraContent = null,
    // Page-specific action buttons rendered in the header's right-side group,
    // immediately to the left of the standard "Add" button. Each item:
    //   { key, label (i18n key), icon, onClick,
    //     color?, variant?, disabled? }
    // Used for one-off actions like "Rapor" / "İndir" that belong with the
    // table toolbar rather than above it.
    extraHeaderActions = [],
    // Sayfalamayi tablonun USTUNDE de goster (opt-in). Uzun listelerde
    // sayfa degistirmek icin en alta inmek gerekmesin diye; varsayilan
    // kapali, diger tablolarin gorunumu degismez.
    topPagination = false,
  } = props;

  // Local state management
  const [filters, setFilters] = useState([]);
  const [localCurrentPage, setLocalCurrentPage] = useState(currentPage);
  // Client-side page size — initialized from prop, mutable via the selector.
  const [localRowsPerPage, setLocalRowsPerPage] = useState(rowsPerPage);
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEditData, setCurrentEditData] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteItemData, setDeleteItemData] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check if row actions should be shown
  const showActions = rowActions 
    ? (typeof rowActions === 'function' ? true : rowActions.length > 0)
    : false;

  useEffect(() => {
    if (filterConfigs.length > 0 && data.length > 0) {
      const generatedFilters = generateFiltersFromData(data, filterConfigs, t);
      setFilters(prev => {
        if (!prev || prev.length === 0) {
          return generatedFilters;
        }

        const prevMap = new Map(
          prev.map(f => [f.id || f.field, f])
        );

        return generatedFilters.map(f => {
          const key = f.id || f.field;
          const prevFilter = prevMap.get(key);
          return prevFilter
            ? { ...f, value: prevFilter.value }
            : f;
        });
      });
    }
  }, [data, filterConfigs, t]);

  useEffect(() => {
    if (tags && tags.length > 0 && filterConfigs.length > 0) {
      // Sadece tabloda gerçekten kullanılan etiketleri göster (yoksa tüm şirket etiketleri)
      const usedTagIds = new Set();
      data.forEach(item => {
        (item.tags || []).forEach(tag => {
          if (tag?.id != null) usedTagIds.add(tag.id);
        });
      });

      const sourceTags =
        usedTagIds.size > 0
          ? tags.filter(tag => usedTagIds.has(tag.id))
          : tags;

      setFilters(prev =>
        prev.map(filter => {
          if (filter.field === 'tags') {
            return {
              ...filter,
              options: sourceTags.map(tag => ({
                value: tag.id,
                label: tag.name,
              })),
            };
          }
          return filter;
        })
      );
    }
  }, [tags, filterConfigs, data]);

  const handleFilterChange = (filterId, value) => {
    setFilters(prev =>
      prev.map(filter =>
        filter.id === filterId ? { 
          ...filter, 
          value: value && (Array.isArray(value) ? (value.length > 0 ? value : '') : value) 
        } : filter
      )
    );
    // Filtre değişince her zaman 1. sayfaya dön
    setLocalCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters(prev => prev.map(filter => {
      if (filter.filterType === 'daterange') {
        return { ...filter, value: { startDate: null, endDate: null } };
      }
      return { ...filter, value: '' };
    }));
    // Filtreler temizlenince de 1. sayfaya dön
    setLocalCurrentPage(1);
  };

  // Memoize so the bubble-up effect below can compare by reference / JSON.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const localFilters = filters.reduce((acc, filter) => {
    if (filter.value) {
      if (filter.filterType === 'daterange') {
        // Only add date range filter if both dates are selected
        if (filter.value.startDate && filter.value.endDate) {
          acc[filter.field] = filter.value;
        }
      } else if (Array.isArray(filter.value)) {
        // For multiselect filters, only add if there are selected values
        if (filter.value.length > 0) {
          acc[filter.field] = filter.value;
        }
      } else {
        // For other filters, add if value is not empty
        acc[filter.field] = filter.value;
      }
    }
    return acc;
  }, {});

  const getValue = (row, field) => {
    if (!field) return undefined;
    // searchFields entries hem string ('jobTitle') hem object form
    // ({field: 'jobTitle', hasuraField: 'job.job_title', type: 'int'})
    // olabiliyor — object form server-side filter için kullanılıyor ama
    // server-pagination kapalıyken bu fonksiyon client-side'da çalışıyor
    // ve object üzerinde .split denerse TypeError atıyordu (Job Assignments
    // sayfası bu yüzden search'te patlıyordu). Path olarak object'in
    // `field` property'sini al — client-side row[displayField] üzerinden eşleşir.
    const path = typeof field === 'string' ? field : (field?.field || null);
    if (!path) return undefined;
    // Support nested fields like 'contractInfo.firmName' or 'user.first_name'
    return path.split('.').reduce((acc, key) => (acc ? acc[key] : undefined), row);
  };

  // Server-side filter/search bubble-up. When the parent opted into
  // serverFilters, push localFilters + localSearchValue out so the page can
  // translate them into a Hasura `_bool_exp`. JSON.stringify gives us a stable
  // signature for the dep array (localFilters is a fresh object every render).
  // Search input is debounced (300 ms) so per-keystroke typing doesn't flood
  // the backend; filter UI changes (dropdowns, date pickers) flush instantly.
  const localFiltersKey = serverFilters ? JSON.stringify(localFilters) : '';
  useEffect(() => {
    if (!serverFilters || !onFiltersChange) return;
    const handle = setTimeout(() => {
      onFiltersChange(localFilters, localSearchValue);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFilters, localFiltersKey, localSearchValue]);

  // When filtering happens on the backend, the parent has already issued a
  // query with the matching `where` clause; the rows in `data` are the
  // authoritative result for the current page. Re-applying the client-side
  // matcher here would silently drop rows whose `searchFields[]` reference
  // transformed/derived field names that don't survive the Hasura round-trip
  // (e.g. `firm_name` is added by transformData, not present on a raw row
  // until then). So in serverFilters mode we pass the server payload through
  // verbatim.
  const filteredData = serverFilters
    ? data
    : data.filter(item => {
        for (const [field, value] of Object.entries(localFilters)) {
          if (value) {
            if (field === 'tags') {
              const jobTags = item.tags || [];
              const selectedTagIds = Array.isArray(value) ? value : [value];

              if (selectedTagIds.length === 0) {
                continue;
              }

              const hasMatchingTag = selectedTagIds.some(selectedTagId =>
                jobTags.some(tag => tag.id === parseInt(selectedTagId))
              );
              if (!hasMatchingTag) {
                return false;
              }
            } else if (value && typeof value === 'object' && value.startDate && value.endDate) {
              // Date range filtering — works for any date field
              const itemDate = new Date(item[field]);
              if (isNaN(itemDate.getTime())) return false;
              const startDate = new Date(value.startDate);
              const endDate = new Date(value.endDate);

              // Set time to start of day for start date and end of day for end date
              startDate.setHours(0, 0, 0, 0);
              endDate.setHours(23, 59, 59, 999);

              if (itemDate < startDate || itemDate > endDate) {
                return false;
              }
            } else if (Array.isArray(value) && value.length > 0) {
              const itemValue = item[field];
              if (Array.isArray(itemValue)) {
                if (!value.some(v => itemValue.includes(v))) return false;
              } else {
                if (!value.includes(itemValue)) return false;
              }
            } else if (item[field] !== value) {
              return false;
            }
          }
        }

        if (localSearchValue && searchFields.length > 0) {
          const searchLower = normalizeSearchString(localSearchValue);
          const hasMatch = searchFields.some(field => {
            const fieldValue = getValue(item, field);
            return (
              fieldValue &&
              normalizeSearchString(fieldValue).includes(searchLower)
            );
          });
          if (!hasMatch) return false;
        }

        return true;
      });

  // Global sorting (before pagination)
  const [sortConfig, setSortConfig] = useState({
    field: null,
    direction: 'asc',
  });

  const sortedData = React.useMemo(() => {
    if (!sortConfig.field) return filteredData;

    const { field, direction } = sortConfig;
    const sorted = [...filteredData].sort((a, b) => {
      const aVal = getValue(a, field);
      const bVal = getValue(b, field);

      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Try date comparison
      const aDate = new Date(aVal);
      const bDate = new Date(bVal);
      const aIsValidDate = !isNaN(aDate.getTime());
      const bIsValidDate = !isNaN(bDate.getTime());

      if (aIsValidDate && bIsValidDate) {
        return direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Number comparison
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // Fallback to string comparison
      const aStr = String(aVal);
      const bStr = String(bVal);
      return direction === 'asc'
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return sorted;
  }, [filteredData, sortConfig]);

  const handleSort = (field) => {
    setSortConfig((prev) => {
      if (prev.field === field) {
        return {
          field,
          direction: prev.direction === 'asc' ? 'desc' : 'asc',
        };
      }
      return { field, direction: 'asc' };
    });
    // Sayfa değişince en baştan başla
    setLocalCurrentPage(1);
  };

  // When server pagination is on, the hook has already sliced the data to
  // the current page and the sortedData array IS the current page. We still
  // run search/filter/sort over it for within-page refinement, but we must
  // NOT slice it again client-side. The pagination UI uses serverTotalCount
  // + serverPage + serverPageSize instead of the local state.
  const effectivePage = serverPagination ? serverPage : localCurrentPage;
  const effectivePageSize = serverPagination ? serverPageSize : localRowsPerPage;
  const totalRows = serverPagination ? serverTotalCount : sortedData.length;

  const paginatedData = serverPagination
    ? sortedData
    : (() => {
        const startIndex = (localCurrentPage - 1) * localRowsPerPage;
        const endIndex = startIndex + localRowsPerPage;
        return sortedData.slice(startIndex, endIndex);
      })();

  const handlePageChange = newPage => {
    if (serverPagination) {
      onServerPageChange && onServerPageChange(newPage);
    } else {
      setLocalCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = newSize => {
    if (serverPagination) {
      onServerPageSizeChange && onServerPageSizeChange(newSize);
    } else {
      setLocalRowsPerPage(newSize);
      setLocalCurrentPage(1);
    }
  };

  const handleSearchChange = value => {
    setLocalSearchValue(value);
    setLocalCurrentPage(1);
  };

  const handleAddClick = () => {
    if (onAddButtonClick) {
      onAddButtonClick();
      return;
    }
    setIsEditMode(false);
    setCurrentEditData(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (row, rowIndex) => {
    setIsEditMode(true);
    setCurrentEditData(row);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setCurrentEditData(null);
  };

  const handleFormSubmit = async formData => {
    if (onFormSubmit) {
      const result = await onFormSubmit(formData, isEditMode, currentEditData);
      // Handle both object result { success: true } and boolean result
      const success = result?.success !== undefined ? result.success : result;
      if (success) {
        handleModalClose();
      }
    }
  };

  const handleDeleteClick = (row, rowIndex) => {
    setDeleteItemData(row);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (onDelete && deleteItemData) {
      setIsDeleting(true);
      try {
        await onDelete(deleteItemData);
        setIsDeleteModalOpen(false);
        setDeleteItemData(null);
      } catch (error) {
        console.error('Delete error:', error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setDeleteItemData(null);
  };

  useImperativeHandle(ref, () => ({
    handleModalClose,
  }));

  const processedColumns = columns.map(col => ({
    ...col,
    title: col.headerName ? t(col.headerName) : col.title || col.headerName,
    render: col.render
      ? (value, row, rowIndex) => {
          const renderedValue = col.render(value, row, rowIndex, t);
          if (
            typeof renderedValue === 'string' &&
            renderedValue.startsWith('table.')
          ) {
            return t(renderedValue);
          }
          return renderedValue;
        }
      : undefined,
  }));

  const handleRowAction = (actionType, row, rowIndex) => {
    if (onRowAction) {
      const result = onRowAction(actionType, row, rowIndex);
      if (result === false) {
        switch (actionType) {
          case 'edit':
            handleEditClick(row, rowIndex);
            break;
          case 'delete':
          case 'remove':
            handleDeleteClick(row, rowIndex);
            break;
          default:
            break;
        }
      }
    } else {
      switch (actionType) {
        case 'edit':
          handleEditClick(row, rowIndex);
          break;
        case 'delete':
        case 'remove':
          handleDeleteClick(row, rowIndex);
          break;
        default:
          break;
      }
    }
  };

  return (
    <Box sx={dataTableContainerStyles.container}>
      <Box sx={dataTableContainerStyles.header}>
        <Box sx={dataTableContainerStyles.headerLeft}>
          {title && (
            <Typography variant="h6" sx={dataTableContainerStyles.title}>
              {title}
            </Typography>
          )}
          {showSearch && (
            <SearchBar
              value={localSearchValue}
              onChange={handleSearchChange}
              placeholder={t(searchPlaceholder)}
            />
          )}
        </Box>

        <Box sx={dataTableContainerStyles.headerRight}>
          {headerExtraContent}
          {exportButton && (
            <MyButton
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={async () => {
                if (onExport) {
                  onExport();
                  return;
                }
                // When server pagination is on, `filteredData` is only the
                // current page. Use the parent-provided `onFetchAllForExport`
                // to grab every row matching the active filters before
                // generating the CSV.
                let exportRows = filteredData;
                if (serverPagination && typeof onFetchAllForExport === 'function') {
                  try {
                    const all = await onFetchAllForExport();
                    // Empty array da geçerli sonuç olabilir (filtre eşleşmesi yok);
                    // tek koşul Array olması. Eskiden length>0 kontrolü vardı,
                    // 0 dönerse mevcut sayfa ile karışık çıktıya yol açıyordu.
                    if (Array.isArray(all)) {
                      exportRows = all;
                    }
                  } catch (err) {
                    console.error('fetchAllForExport failed:', err);
                    // Kullanıcı export sonucu eksik gördüğünü düşünmesin
                    // diye en azından konsolda hata net görünür.
                  }
                }
                exportTableToCSV(
                  exportRows,
                  columns,
                  exportFilename || `${entityType}_${new Date().toISOString().slice(0, 10)}.csv`,
                  t,
                  formFields
                );
              }}
              disabled={exportDisabled || loading || (filteredData.length === 0 && !serverPagination)}
              sx={{ mr: 1 }}
            >
              {t(exportLabel) || exportLabel}
            </MyButton>
          )}
          {Array.isArray(extraHeaderActions) && extraHeaderActions.map((action) => (
            <MyButton
              key={action.key}
              onClick={action.onClick}
              startIcon={action.icon}
              color={action.color || 'primary'}
              variant={action.variant || 'outlined'}
              disabled={action.disabled}
              size="small"
            >
              {t(action.label)}
            </MyButton>
          ))}
          {showAddButton && (
            <MyButton
              variant="contained"
              startIcon={addButtonIcon}
              onClick={handleAddClick}
              sx={{ ...dataTableContainerStyles.addButton, ...addButtonSx }}
            >
              {addButtonText}
            </MyButton>
          )}
        </Box>
      </Box>
      {showFilters && filters.length > 0 && (
        <Box sx={dataTableContainerStyles.filtersContainer}>
          <TableFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
          />
        </Box>
      )}
      {showPagination && topPagination && (
        <TablePagination
          totalRows={totalRows}
          currentPage={effectivePage}
          rowsPerPage={effectivePageSize}
          onPageChange={handlePageChange}
          showPageSizeSelector
          pageSizeOptions={[20, 50, 100]}
          onPageSizeChange={handlePageSizeChange}
        />
      )}
      <Box sx={dataTableContainerStyles.tableWrapper}>
        {loading ? (
          <TableLoading
            columns={processedColumns}
            rows={loadingRows}
            showActions={showActions}
          />
        ) : (
          <MyTable
            columns={processedColumns}
            data={paginatedData}
            showActions={showActions}
            rowActions={rowActions}
            onRowAction={handleRowAction}
            onSort={handleSort}
            onRowClick={onRowClick}
            getRowSx={getRowSx}
          />
        )}
      </Box>
      <Box sx={dataTableContainerStyles.paginationWrapper}>
        {showPagination && (
          <TablePagination
            totalRows={totalRows}
            currentPage={effectivePage}
            rowsPerPage={effectivePageSize}
            onPageChange={handlePageChange}
            showPageSizeSelector
            pageSizeOptions={[20, 50, 100]}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </Box>

      {formFields.length > 0 && (
        <MyModal
          open={isModalOpen}
          onClose={handleModalClose}
          title={isEditMode ? t(editModalTitle) : t(addModalTitle)}
          maxWidth="md"
          disableBackdropClose
          disableEscapeKeyDown
        >
          <DynamicForm
            fields={formFields}
            initialData={
              isEditMode
                ? transformEditData
                  ? transformEditData(currentEditData)
                  : currentEditData
                : {}
            }
            onSubmit={handleFormSubmit}
            onCancel={handleModalClose}
            submitText={
              isEditMode ? t('form.buttons.update') : t('form.button.save')
            }
            cancelText={t('form.buttons.cancel')}
            loading={submitting}
            entityId={isEditMode ? currentEditData?.id : null}
            commentEntityType={isEditMode ? commentEntityType : null}
            renderFieldExtras={renderFieldExtras}
          />
        </MyModal>
      )}
      <DeleteConfirmModal
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        itemName={deleteItemData?.name || deleteItemData?.title || ''}
        loading={isDeleting || deleting}
        entityType={entityType}
        icon={deleteModalIcon}
        confirmText={deleteConfirmText}
      />
    </Box>
  );
});

export default DataTableContainer;
